import React, { useCallback, useMemo } from "react";
import { Text, Button } from "@radix-ui/themes";
import { useImproveChat } from "./ImproveChatWrapper";
import { ImproveChatMessages } from "./ImproveChatMessages";
import styles from "./ImproveChat.module.scss";
import { ImproveChatInput } from "./ImproveChatInput";
import { ChainOfThoughtStep } from "@/components/ai-elements/chain-of-thought-for-improve-chat";
import { AddCommentIcon } from "@/v2/assets/icons/AddCommentIcon";

export function ImproveChat() {
  const { messages, input, setInput, handleSubmit, status, isDisabled } =
    useImproveChat();
  const isLoading = status === "submitted" || status === "streaming";

  const onSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }
      if (!input?.trim() || isLoading) return;
      handleSubmit();
    },
    [input, isLoading, handleSubmit]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  const showWelcome = useMemo(() => messages.length === 0, [messages.length]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollableContent}>
        {showWelcome && (
          <ChainOfThoughtStep label="Improve this answer" status="active">
            <Text as="p" size="3" className={styles.welcomeText}>
              Let&apos;s make this answer better. What would you like to change
              or improve about it?
            </Text>
          </ChainOfThoughtStep>
        )}

        <ImproveChatMessages
          messages={messages}
          isLoading={isLoading}
          isDisabled={isDisabled}
        />
      </div>

      <div className={styles.inputArea}>
        <ImproveChatInput
          input={input}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onChange={setInput}
          onKeyDown={onKeyDown}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
}

export const ImproveChatResetButton = () => {
  const { reset } = useImproveChat();
  return (
    <Button
      variant="outline"
      onClick={reset}
      style={{
        position: "absolute",
        right: 0,
        top: "4px",
      }}
    >
      <AddCommentIcon size={12} />
    </Button>
  );
};
